import {
  ArrowDown,
  ArrowRight,
  CalendarClock,
  CircleCheck,
  HeartHandshake,
  Landmark,
  NotebookPen,
  PiggyBank,
} from "lucide-react";
import {
  DomainCard,
  Eyebrow,
  HandHeart,
  HandNote,
  IconTile,
  LayerCard,
  type Domain,
} from "@appcaire/ui";
import type { LucideIcon } from "lucide-react";
import { HomeLink } from "./HomeParts";
import { SharedPlanScene } from "./SharedPlanScene";
import { brandHomeCopy } from "./brandHomeCopy";
import type { LandingLocale } from "../landingCopy";
import {
  homeSectionClass,
  homeSectionHeadClass,
  homeSectionTitleClass,
} from "./HomeParts";

/** Content model order and colours are fixed: Beviljat → Utfört. */
const LAYERS: { domain: Domain; icon: LucideIcon }[] = [
  { domain: "continuity", icon: Landmark },
  { domain: "human", icon: NotebookPen },
  { domain: "planning", icon: CalendarClock },
  { domain: "care", icon: CircleCheck },
];

/** "Så funkar det" as four layers, one visit followed from decision to performed (3a). */
export function FourLayersSection({
  locale,
  showLink = true,
}: {
  locale: LandingLocale;
  /** Off on /sa-fungerar-caire, which the link points to and which tells
   * the shared plan itself. */
  showLink?: boolean;
}) {
  const c = brandHomeCopy[locale].layers;
  return (
    <section
      className={homeSectionClass}
      aria-labelledby="four-layers-heading"
      data-home-section="how"
    >
      <div className={homeSectionHeadClass}>
        <div>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 id="four-layers-heading" className={homeSectionTitleClass}>
            {c.title}
          </h2>
        </div>
        <p>{c.lead}</p>
      </div>
      <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" role="list">
        {c.items.map((layer, i) => (
          <li key={layer.title} className="relative flex flex-col gap-2">
            <LayerCard
              n={i + 1}
              domain={LAYERS[i].domain}
              icon={LAYERS[i].icon}
              label={`${i + 1} · ${layer.label}`}
              title={layer.title}
              rule={layer.text}
              cadence={{ label: c.cadenceLabel, text: layer.cadence }}
              examples={layer.items.map((item) => ({
                title: item.name,
                detail: item.value,
              }))}
            />
            <HandNote tone={LAYERS[i].domain} tilt={i % 2 ? 1 : -1} decorative>
              {layer.note}
              {i === 3 ? <HandHeart index={i} className="ml-1" /> : null}
            </HandNote>
            {/* One phase leads to the next: 1 → 2 → 3 → 4. */}
            {i < c.items.length - 1 ? (
              <>
                <span
                  aria-hidden="true"
                  className="absolute -right-4 top-16 z-10 hidden h-8 w-8 place-items-center rounded-full border border-caire-ink/10 bg-caire-surface text-caire-ink shadow-sm lg:grid"
                >
                  <ArrowRight size={16} />
                </span>
                <span
                  aria-hidden="true"
                  className="mx-auto grid h-8 w-8 place-items-center text-caire-muted sm:hidden"
                >
                  <ArrowDown size={18} />
                </span>
              </>
            ) : null}
          </li>
        ))}
      </ol>
      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <DomainCard
          domain="care"
          className="flex-row flex-wrap items-center gap-5"
        >
          <IconTile icon={PiggyBank} domain="care" size="s" />
          <div className="flex min-w-[16rem] flex-1 flex-col gap-1">
            <h3 className="text-[19px] font-extrabold text-domain-care-ink">
              {c.pool.title}
            </h3>
            <p className="text-[15px] leading-normal">{c.pool.text}</p>
          </div>
          <div className="flex flex-col items-end">
            <span className="font-mono text-[32px] font-semibold text-domain-care-ink">
              {c.pool.value}
            </span>
            <span className="font-mono text-xs text-domain-care-ink">
              {c.pool.valueLabel}
            </span>
          </div>
        </DomainCard>
        <DomainCard domain="human" surface="white" className="gap-2">
          <div className="flex items-center gap-3">
            <IconTile
              icon={HeartHandshake}
              domain="human"
              size="xs"
              variant="tint"
            />
            <h3 className="text-[19px] font-extrabold">{c.priority.title}</h3>
          </div>
          <p className="text-[15px] leading-normal">{c.priority.text}</p>
        </DomainCard>
      </div>
      {showLink ? <SharedPlanScene locale={locale} copy={c.sync} /> : null}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-[60ch] text-[17px]">{c.shared}</p>
        {showLink ? (
          <HomeLink to="saFungerarCaire" locale={locale}>
            {c.link}
          </HomeLink>
        ) : null}
      </div>
      <p className="mt-2 text-xs text-caire-muted">{c.example}</p>
    </section>
  );
}
